











































































































































import { v4 as uuidv4 } from 'uuid';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Subscription } from 'rxjs';
import SearchAndOrder from '@core/interfaces/bpc-search-and-order';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';

@Component({
  name: 'bpc-export-modal',
  components: {},
})
export default class AsyncExportModal extends Vue {
  // Props
  @Prop({ type: String, required: true }) readonly entityName: string;
  @Prop({ type: String, default: 'export-confirmation-modal' }) readonly modalId: string;
  @Prop({ type: String, default: 'export' }) readonly exportConfigName: string;
  @Prop({ type: Boolean, default: false }) readonly exportAllAllowed: boolean;
  @Prop({ type: Boolean, default: false }) readonly showModal: boolean;
  @Prop({ type: Object, default: null }) readonly filters: object;
  @Prop({ type: Object, default: null }) readonly searchAndOrder: SearchAndOrder;
  @Prop({ type: Array, default: () => [] }) readonly selectedEntities: string[];
  @Prop({ type: Number, default: 0 }) readonly totalEntities: number;
  @Prop({ type: Boolean, default: false }) readonly checkedAll: boolean;
  @Prop({ type: Array, default: () => [] }) readonly dataToShow: [string[]];
  @Prop({ type: String, default: null }) readonly configFileNameForExport: string;
  @Prop({ type: String, default: null }) readonly exportPath: string;
  @Prop({ type: String, default: null }) readonly downloadPath: string;

  // Data
  public isExporting: boolean = false;
  public isCancelling: boolean = false;
  public exportEventsError: object[] = [];
  public exportEventsSuccess: number = 0;
  public exportRequestSent: boolean = false;
  public reportPerPage: number = 20;
  public reportCurrentPage: number = 1;
  public exportSubscription: Subscription;
  public sessionCode: string;
  public jobId: string = null;
  public selectedFileType: string = 'xlsx';
  public processAll: boolean = false;

  // Lifecycle
  mounted() {
    if (this.showModal) {
      this.openModal();
    }
  }

  // Computed
  get tenantId() {
    return this.$store.getters['tenant/selectedTenantId'];
  }

  get exportModalTitle() {
    return this.$t('global.modals.export.title', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  get exportModalBody() {
    return this.$t('global.modals.export_all.async.body', {
      entityName: this.$t('global.models.' + this.entityName + '.name'),
      entityPluralName: this.$t('global.models.' + this.entityName + '.pluralName'),
      thisEntityName: this.$t('global.models.' + this.entityName + '.thisName'),
      thisEntityPluralName: this.$t('global.models.' + this.entityName + '.thisPluralName'),
    });
  }

  get reportRows() {
    return this.exportEventsError.length;
  }

  get reportCurrentList() {
    const start = this.reportCurrentPage * this.reportPerPage - this.reportPerPage;
    const end = start + this.reportPerPage;
    return this.exportEventsError.slice(start, end);
  }

  get userLang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  // Methods
  openModal(): void {
    this.resetModal();
    this.$bvModal.show(this.modalId);
  }
  onCloseModal(): void {
    this.$emit('close-modal');
  }
  closeModal(): void {
    this.$bpcEcho.leaveExportChannel(this.sessionCode);
    this.resetModal();
    this.$bvModal.hide(this.modalId);
  }
  cancelExport(): void {
    this.isCancelling = true;
    const basePath = this.$bpcApiRouteHelper.getEntityPath(this.entityName);
    this.$bpcApi
      .post(basePath + '/cancel-export/' + this.jobId, {
        sessionCode: this.sessionCode,
      })
      .subscribe(
        (res: BPCGenericApiResponse) => {
          this.$emit('export-cancelled');
          this.onCloseModal();
        },
        error => {
          this.$emit('error-cancelling-export');
          this.jobId = null;
        },
      );
  }

  resetModal(): void {
    this.exportEventsError = [];
    this.exportEventsSuccess = 0;
    this.exportRequestSent = false;
    this.isExporting = false;
    this.isCancelling = false;
    this.sessionCode = null;
    this.jobId = null;
    this.selectedFileType = 'xlsx';
    // NOTE important to reset this value otherwise the next time the modal
    // is opened it will be potentially true
    this.processAll = false;
  }

  onExport() {
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
      this.$bpcEcho.leaveExportChannel(this.sessionCode);
    }

    this.sessionCode = this.getSessionCode();
    this.listenToWebsocketExportChannel(this.sessionCode);
    const exportPath = this.exportPath
      ? this.exportPath
      : `${this.$bpcApiRouteHelper.getEntityPath(this.entityName)}/export`;
    const request = this.$bpcApi.post(exportPath, {
      selectedItems: this.selectedEntities,
      filters: this.filters,
      searchAndOrder: this.searchAndOrder,
      processAll: this.processAll,
      sessionCode: this.sessionCode,
      userLang: this.userLang,
      os: this.getUserOS(),
      fileType: this.selectedFileType,
      configFileNameForExport: this.configFileNameForExport,
      exportConfigName: this.exportConfigName,
    });

    this.exportSubscription = request.subscribe(
      (res: BPCGenericApiResponse) => {
        this.exportRequestSent = true;
        this.$emit('request-sent');
      },
      error => {
        this.exportRequestSent = false;
        this.isExporting = false;
        this.$emit('error-sending-request');
        this.$bpcEcho.leaveExportChannel(this.sessionCode);
        this.resetModal();
      },
    );
  }

  getSessionCode(): string {
    return `export-${this.tenantId}-${uuidv4()}`;
  }

  listenToWebsocketExportChannel(sessionCode: string): void {
    this.isExporting = true;
    this.$bpcEcho
      .listenExportChannel(sessionCode)
      .listen('.BPC\\Core\\Events\\EntitiesExportedToCSV', async data => {
        this.$bpcMessage.showInfoMessage({
          text: 'global.export.entities_exported',
        });
        this.isExporting = false;
        this.$bpcEcho.leaveExportChannel(sessionCode);
        await this.downloadExportedFile(data.filePath, data.fileType);
        this.$emit('exported');
      })
      .listen('.BPC\\Core\\Events\\ChunkOfEntitiesExportedToCSV', data => {
        if (!this.jobId) {
          this.jobId = data.jobId;
        }
        this.exportEventsSuccess = this.exportEventsSuccess + Number(data.processedEntities);
      })
      .listen('.BPC\\Core\\Events\\ErrorExportingEntityToCSV', data => {
        if (!this.jobId) {
          this.jobId = data.jobId;
        }
        this.exportEventsError.push(data);
      });
  }

  async downloadExportedFile(filePath: string, fileType: string = 'csv') {
    return new Promise((resolve, reject) => {
      const downloadPath = this.downloadPath
        ? this.downloadPath
        : `${this.$bpcApiRouteHelper.getEntityPath(this.entityName)}/download-export`;
      this.$bpcApi
        .download(`${downloadPath}`, `${this.entityName}-export.${fileType}`, {
          params: {
            filePath: filePath,
            fileType: fileType,
          },
        })
        .subscribe(
          (res: BPCGenericApiResponse) => {
            // Download the response file
            // this.$emit('on-export-items');
            // this.downloadingExport = false;
            resolve(res);
          },
          error => {
            reject(error);
          },
        );
    });
  }

  getUserOS(): string {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
    let os = null;

    if (macosPlatforms.indexOf(platform) !== -1) {
      os = 'Mac OS';
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      os = 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      os = 'Windows';
    } else if (/Android/.test(userAgent)) {
      os = 'Android';
    } else if (/Linux/.test(platform)) {
      os = 'Linux';
    }

    return os;
  }

  // Watchers
  @Watch('showModal')
  onShowModalChange(val) {
    if (val) {
      this.openModal();
    } else {
      this.closeModal();
    }
  }
}
